// services/firebaseProducts.js
import { 
  collection, 
  getDocs, 
  query, 
  doc, 
  getDoc,
where, limit,
} from "firebase/firestore";
import { db } from "../db/firebase";

// Cache for categories to avoid multiple fetches
let categoriesCache = null;

export const fetchCategories = async () => {
  if (categoriesCache) {
    return categoriesCache;
  }

  try {
    const categoriesRef = collection(db, "categories");
    const querySnapshot = await getDocs(categoriesRef);
    const categories = {};
    
    querySnapshot.forEach((doc) => {
      categories[doc.id] = {
        id: doc.id,
        ...doc.data()
      };
    });
    
    categoriesCache = categories;
    return categories;
  } catch (error) {
    console.error("Error fetching categories:", error);
    return {};
  }
};

let productsCache = null;


// OPTIMIZED: Only essential fields for Shop page
// Update in services/firebaseProducts.js
export const fetchShopProducts = async () => {
  if (productsCache) return productsCache;

  try {
    const productsRef = collection(db, "products");
    const q = query(productsRef);
    const querySnapshot = await getDocs(q);

    const categories = await fetchCategories();
    const products = querySnapshot.docs.map(doc => {
      const productData = doc.data();
      
      return {
        id: doc.id,
        name: productData.PName || "Unnamed Product",
        PName: productData.PName,
        PCategories: productData.PCategories || [],
        // NEW: Include order field
        order: productData.order,
        PType: (productData.PType || []).map(variant => ({
          id: variant.id,
          PTypeName: variant.PTypeName,
          PSoleColor: variant.PSoleColor,
          PPrice: variant.PPrice,
          PInOffer: variant.PInOffer,
          PDiscountPrice: variant.PDiscountPrice,
          PImgUrls: variant.PImgUrls?.[0] ? [variant.PImgUrls[0]] : [],
          PStock: variant.PStock || []
        })),
        PRating: productData.PRating || 0,
        PDateAdded: productData.PDateAdded,
        category: (productData.PCategories || [])
          .map(catId => categories[catId]?.CategoryName)
          .filter(Boolean)[0] || "Uncategorized",
      };
    });

    productsCache = products;
    return products;
  } catch (error) {
    console.error("Error fetching shop products:", error);
    return [];
  }
};

export const fetchAllProducts = async () => {
  if (productsCache) return productsCache;

  try {
    const productsRef = collection(db, "products");
    const q = query(productsRef);
    const querySnapshot = await getDocs(q);

    const categories = await fetchCategories();
    const products = querySnapshot.docs.map(doc => {
      const productData = doc.data();
      const categoryNames = (productData.PCategories || [])
        .map(catId => categories[catId]?.CategoryName)
        .filter(Boolean);

      return {
        id: doc.id,
        name: productData.PName || "Unnamed Product",
        category: categoryNames[0] || "Uncategorized",
        ...productData,
      };
    });

    productsCache = products;
    return products;
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

// =================================================================
// SIMPLE: fetchFeaturedProducts - relies on hasActiveOffer field accuracy
// =================================================================
export const fetchFeaturedProducts = async () => {
  try {
    const productsRef = collection(db, "products");
    const q = query(
      productsRef, 
      where("hasActiveOffer", "==", true),
      limit(4)
    );

    const querySnapshot = await getDocs(q);
    const categories = await fetchCategories();
    
    const featuredProducts = querySnapshot.docs.map(doc => {
      const productData = doc.data();
      const categoryNames = (productData.PCategories || [])
        .map(catId => categories[catId]?.CategoryName)
        .filter(Boolean);

      return {
        id: doc.id,
        name: productData.PName || "Unnamed Product",
        category: categoryNames[0] || "Uncategorized",
        ...productData,
      };
    });

    console.log(`Fetched ${featuredProducts.length} featured products using hasActiveOffer query`);
    return featuredProducts;

  } catch (error) {
    console.error("Error fetching featured products:", error);
    return [];
  }
};

// Existing getCategoryNameById function remains the same...
export const getCategoryNameById = async (categoryId) => {
  const categories = await fetchCategories();
  return categories[categoryId]?.CategoryName || "Uncategorized";
};